import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { KompaktGrundstueckPipe } from '../../kompaktdarstellung/grundstueck/kompakt-grundstueck.pipe';
import { KompaktGrundstueckbeschreibungPipe } from '../../kompaktdarstellung/grundstueck-beschreibung/kompakt-grundstueckbeschreibung.pipe';
import { ComparatorService } from '../../services/comparator-service';
import { GrundstueckListEntry } from '../../../core/gb-api';
import { FormatConfigService } from '../../../core/services/format-config.service';
import { TableSortModel } from '../../tablesort/tableSortModel';
import { AbstractComponent } from '../../components/abstract-component/abstract.component';
import { ListLoader } from '../../components/list-loader/list-loader';
import { DokulinkItemBuilderFactory } from '../../components/dokulink/dokulinkItemBuilderFactory';
import { DokulinkItem } from 'app/shared/components/dokulink/dokulinkItem';
import { GrundstueckDokuLinkItemBuilder } from 'app/shared/components/dokulink/dokulinkItemBuilder';
import { GrundstuecknummerPipe } from '../../pipes/simple/grundstuecknummer.pipe';
import { ListLoaderWrapperComponent } from '../../components/list-loader-wrapper/list-loader-wrapper.component';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ColumnHeaderComponent } from '../../tablesort/sort-icon/column-header.component';
import { HistoryDirective } from '../../directives/history.directive';
import { LinkComponent } from '../../components/link/link.component';
import { DokulinkComponent } from '../../components/dokulink/dokulink.component';
import { MapsComponent } from '../maps/maps.component';
import { AuszugComponent } from '../../components/auszug/auszug.component';
import { CmisComponent } from '../../components/cmis/cmis.component';

export type GrundstueckSelection = {
  index?: number;
  grundstueckIds: string[];
};

@Component({
  selector: 'cap-grundstueck-table',
  templateUrl: './grundstueck-table.component.html',
  styleUrls: ['./grundstueck-table.component.scss'],
  imports: [
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    ColumnHeaderComponent,
    HistoryDirective,
    LinkComponent,
    DokulinkComponent,
    CmisComponent,
    MapsComponent,
    AuszugComponent,
    TranslatePipe,
    AsyncPipe,
    GrundstuecknummerPipe
  ]
})
export class GrundstueckTableComponent extends AbstractComponent {
  @Input()
  showZeilenSelect = true;

  tableSortModel = new TableSortModel<GrundstueckListEntryVm>('table', this.comparatorService);

  _listLoader: ListLoader<GrundstueckListEntryVm>;
  ids = Array<string>();
  allCheckboxesSelected = false;

  @Input()
  indexNumber: number;

  @Output() grundstueckIdsListEventItem = new EventEmitter<GrundstueckSelection>();

  constructor(
    protected router: Router,
    public appformatConfigService: FormatConfigService,
    private comparatorService: ComparatorService,
    protected dokuLinkBuilderFactory: DokulinkItemBuilderFactory
  ) {
    super();
    keysToTranslate<string>(['KeineDatenVorhanden', 'grundstueck.ListTitel', 'PlanZuGrundstueckAnzeigen', 'Kurzbeschreibung']);
    this.tableSortModel
      .addColumn('nummer', (a, b) => this.comparatorService.grundstueckComparator(a.model, b.model), 'NUMMER')
      .switchSortDirection();
    this.tableSortModel.addColumn('art', (a, b) => this.comparatorService.translatedStringComparator(a.kompaktArt, b.kompaktArt), 'ART');
  }

  @Input()
  set listLoader(value: ListLoader<GrundstueckListEntryVm>) {
    if (!value) {
      return;
    }
    this._listLoader = value;
    this.listLoader.applySortModel(this.tableSortModel);
  }

  get listLoader(): ListLoader<GrundstueckListEntryVm> {
    return this._listLoader;
  }

  addCheckedGrundstueck(item: GrundstueckListEntryVm, event: any, indexNumber: number) {
    const grundstueckId = item.dokulinkItem.id;
    if (event.checked) {
      if (this.ids.includes(grundstueckId)) {
        return;
      }
      this.ids.push(grundstueckId);
    } else {
      this.ids = this.ids.filter((id) => id !== grundstueckId);
    }
    const grundstueckIdsByIndexMap = { index: indexNumber, grundstueckIds: this.ids };
    this.updateGrundstueckIdList(grundstueckIdsByIndexMap);
  }

  updateGrundstueckIdList(grundstueckIdsByIndexMap: GrundstueckSelection) {
    this.grundstueckIdsListEventItem.emit(grundstueckIdsByIndexMap);
  }

  selectAllCheckboxes(items: Array<GrundstueckListEntryVm>, event: any) {
    this.allCheckboxesSelected = !this.allCheckboxesSelected;
    for (let i = 0; i < items.length; i++) {
      this.addCheckedGrundstueck(items[i], event, this.indexNumber);
    }
  }

  removeAllCheckboxes() {
    const selectAllCheckbox = document.querySelector('#selectAllCheckbox') as HTMLInputElement;
    if (selectAllCheckbox) {
      selectAllCheckbox.checked = false;
    }
    this.ids = [];
    this.updateGrundstueckIdList({
      index: this.indexNumber,
      grundstueckIds: []
    });
  }
}

export interface ViewModelCtx {
  translate: TranslateService;
  grundstuecknummerPipe: GrundstuecknummerPipe;
  kompaktGrundstueckPipe: KompaktGrundstueckPipe;
  kompaktGrundstueckBeschreibung: KompaktGrundstueckbeschreibungPipe;
  dokuLinkBuilderFactory: DokulinkItemBuilderFactory;
}

export class GrundstueckListEntryVm {
  dokulinkItem: DokulinkItem;

  constructor(
    public model: GrundstueckListEntry,
    private ctx: ViewModelCtx
  ) {
    this.dokulinkItem = (<GrundstueckDokuLinkItemBuilder>ctx.dokuLinkBuilderFactory.create('GRUNDSTUECK')).setEntry(model).build();
  }

  get kompaktGrundstueck(): string {
    return this.ctx.kompaktGrundstueckPipe.transform(this.model);
  }

  get tooltip(): string {
    return this.ctx.grundstuecknummerPipe.transform(this.model?.grundstueckNummer);
  }

  get kompaktKurzbeschreibung() {
    return this.ctx.kompaktGrundstueckBeschreibung.transform(this.model);
  }

  get kompaktArt() {
    if (this.model.grundstueckArt === 'SDR' && this.model.sdrArt) {
      return this.ctx.translate.instant(this.model.grundstueckArt) + ' (' + this.ctx.translate.instant(this.model.sdrArt) + ')';
    }

    if (this.model.ligArt) {
      return this.ctx.translate.instant(this.model.ligArt + '_lang');
    }

    return this.ctx.translate.instant(this.model.grundstueckArt);
  }
}
